/**
 * StepContext — Etapa: Contexto & Discovery.
 *
 * Etapa de ENTRADA pura: o usuário adiciona notas de discovery e documentos. TODO
 * o conteúdo desta etapa é indexado (RAG) E enviado INTEGRALMENTE ao LLM nas etapas
 * seguintes (via scope_prompt_block — determinístico, não depende do RAG top-k).
 * Aqui NÃO há sugestão de IA — a ideação (próxima etapa) é quem interpreta.
 *
 * Persistência real via:
 *   POST   /projects/{id}/discovery-contexts — notas de discovery
 *   POST   /projects/{id}/documents          — upload de documentos
 *   DELETE /projects/{id}/documents/{docId}  — excluir documento (+ purga RAG)
 *
 * Paleta C, estilos inline, pt-BR, a11y preservados.
 */
import { useState, useRef, useEffect } from 'react';
import {
  FileText, Upload, ArrowRight, Eye, Trash2, CheckCircle2, ShieldCheck,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge } from '@/components/ui';
import { MutationError, QueryError, LoadingSpinner } from '@/components/ui/QueryState';
import {
  useDiscoveryContexts,
  useProjectDocuments,
  useUpsertDiscoveryContext,
  useAddProjectDocument,
  useDeleteProjectDocument,
} from '@/api/projects';
import { useJourney } from '../JourneyContext';

// ─── Upload de documentos ───────────────────────────────────────────────────

/** Extensões aceitas no upload (formatos TEXTO — o backend guarda content_text). */
const ACCEPTED_EXT = [
  '.md', '.markdown', '.txt', '.json', '.yaml', '.yml', '.csv', '.html', '.htm', '.xml',
];
/** Limite de tamanho do arquivo (1 MB de texto). */
const MAX_DOC_BYTES = 1_000_000;

/** Mapeia extensão → content_type. */
function contentTypeFor(nameLower) {
  if (nameLower.endsWith('.json')) return 'application/json';
  if (nameLower.endsWith('.yaml') || nameLower.endsWith('.yml')) return 'application/yaml';
  if (nameLower.endsWith('.csv')) return 'text/csv';
  if (nameLower.endsWith('.html') || nameLower.endsWith('.htm')) return 'text/html';
  if (nameLower.endsWith('.xml')) return 'application/xml';
  if (nameLower.endsWith('.txt')) return 'text/plain';
  return 'text/markdown';
}

/** Tipos de documento — rótulo pt-BR → doc_tag do backend. */
const DOC_TYPE_OPTIONS = [
  { label: 'Histórias',   tag: 'stories' },
  { label: 'UX',          tag: 'ux' },
  { label: 'Arquitetura', tag: 'architecture' },
  { label: 'Segurança',   tag: 'security' },
  { label: 'Compliance',  tag: 'compliance' },
  { label: 'Outro',       tag: 'other' },
];
const TAG_TO_LABEL = Object.fromEntries(DOC_TYPE_OPTIONS.map((o) => [o.tag, o.label]));

function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Selo de status de indexação (RAG) por item. O conteúdo já vai INTEGRAL ao LLM
 *  independentemente disto; o selo reflete só o índice semântico assíncrono. */
function IndexBadge({ indexed }) {
  if (indexed) {
    return (
      <span className="flex items-center gap-1" title="Indexado no RAG semântico">
        <CheckCircle2 size={12} style={{ color: C.cyber }} aria-hidden="true" />
        <Badge color={C.cyber} light>INDEXADO</Badge>
      </span>
    );
  }
  return (
    <span title="Enviado ao LLM integralmente; índice RAG sendo processado">
      <Badge color={C.digital} light>INDEXANDO…</Badge>
    </span>
  );
}

// ─── StepContext ──────────────────────────────────────────────────────────────

export function StepContext({ onNext, onBack }) {
  const { projectId } = useJourney();

  const contextsQuery   = useDiscoveryContexts(projectId);
  const documentsQuery  = useProjectDocuments(projectId);

  const upsertContext   = useUpsertDiscoveryContext(projectId);
  const addDocument     = useAddProjectDocument(projectId);
  const deleteDocument  = useDeleteProjectDocument(projectId);

  const [notes,       setNotes]       = useState('');
  const [docType,     setDocType]     = useState('architecture');
  const [fileError,   setFileError]   = useState('');
  const [previewId,   setPreviewId]   = useState(null);   // doc.id em preview
  const [confirmDel,  setConfirmDel]  = useState(null);   // doc.id aguardando confirmação
  const fileInputRef = useRef(null);

  const docs     = documentsQuery.data ?? [];
  const contexts = contextsQuery.data  ?? [];

  // Polling do status de indexação: enquanto houver item "indexando", refaz as
  // queries a cada 4s para o selo virar "INDEXADO ✓" sem o usuário recarregar.
  const pendingIndex =
    docs.some((d) => d.indexed === false)
    || contexts.some((c) => c.processing_status && c.processing_status !== 'processed');
  useEffect(() => {
    if (!pendingIndex) return undefined;
    const t = setInterval(() => {
      documentsQuery.refetch();
      contextsQuery.refetch();
    }, 4000);
    return () => clearInterval(t);
  }, [pendingIndex, documentsQuery, contextsQuery]);

  async function handleSaveContext() {
    const trimmed = notes.trim();
    if (!trimmed) return;
    // Contrato do backend: kind ('note') + title + summary (o texto da nota).
    const title = (trimmed.split('\n')[0] || 'Nota de discovery').slice(0, 80);
    try {
      await upsertContext.mutateAsync({ kind: 'note', title, summary: trimmed });
      setNotes('');
    } catch { /* tratado pelo MutationError */ }
  }

  async function handleDeleteDoc(docId) {
    try {
      await deleteDocument.mutateAsync(docId);
      if (previewId === docId) setPreviewId(null);
    } catch { /* tratado pelo MutationError */ }
    setConfirmDel(null);
  }

  function handleFilePick(e) {
    setFileError('');
    const f = e.target.files?.[0];
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!ACCEPTED_EXT.some((ext) => lower.endsWith(ext))) {
      setFileError(`Formato não suportado. Use ${ACCEPTED_EXT.join(', ')}.`);
      return;
    }
    if (f.size > MAX_DOC_BYTES) {
      setFileError(`Arquivo muito grande (${formatBytes(f.size)}). Máximo ${formatBytes(MAX_DOC_BYTES)}.`);
      return;
    }
    // Envia AUTOMATICAMENTE ao selecionar — sem passo extra de "Adicionar".
    const reader = new FileReader();
    reader.onload = async () => {
      const content = String(reader.result ?? '');
      try {
        await addDocument.mutateAsync({
          filename:     f.name,
          doc_tag:      docType,
          size_bytes:   f.size,
          content_type: contentTypeFor(lower),
          content_text: content,
        });
      } catch { /* tratado pelo MutationError */ }
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.onerror = () => setFileError('Falha ao ler o arquivo. Tente novamente.');
    reader.readAsText(f);
  }

  return (
    <div>
      {/* Título */}
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: C.neutral, margin: '0 0 6px' }}>
          Contexto & Discovery
        </h2>
        <p style={{ fontSize: 14, color: C.neutralDim, margin: 0 }}>
          Adicione notas de reuniões de discovery e documentos do projeto. Aqui é só
          entrada — a IA interpreta na próxima etapa (Ideação).
        </p>
      </div>

      {/* Selo de garantia: tudo desta etapa é indexado E enviado ao LLM */}
      <div
        role="note"
        className="flex items-start gap-3"
        style={{
          marginBottom: 24, padding: '12px 16px', borderRadius: 10,
          backgroundColor: `${C.cyber}10`, border: `1px solid ${C.cyber}44`,
        }}
      >
        <ShieldCheck size={18} style={{ color: C.cyber, flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
        <div style={{ fontSize: 12.5, color: C.neutralDim, lineHeight: 1.55 }}>
          <strong style={{ color: C.neutral }}>Garantia:</strong> todo o conteúdo desta
          etapa — notas de discovery e documentos — é enviado <strong style={{ color: C.neutral }}>integralmente</strong> ao
          LLM nas etapas seguintes (não depende de busca semântica). O selo{' '}
          <Badge color={C.cyber} light>INDEXADO</Badge> indica que o índice RAG (busca)
          também já ficou pronto.
        </div>
      </div>

      {/* Contextos existentes */}
      {contextsQuery.isLoading && <LoadingSpinner label="Carregando contextos..." size={20} />}
      {contextsQuery.error && (
        <QueryError error={contextsQuery.error} onRetry={() => contextsQuery.refetch()} />
      )}

      {contexts.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          {contexts.map((ctx, i) => (
            <div
              key={ctx.id ?? i}
              style={{
                padding: '12px 16px',
                borderRadius: 8,
                backgroundColor: C.blackElev2,
                border: `1px solid ${C.blackBorder}`,
                marginBottom: 8,
              }}
            >
              <div className="flex items-center gap-2" style={{ fontSize: 12, color: C.neutralMuted, marginBottom: 4 }}>
                <span>
                  {ctx.kind === 'meeting'
                    ? 'REUNIÃO DE DISCOVERY'
                    : ctx.kind === 'upload'
                      ? 'DOCUMENTO'
                      : 'NOTA'}
                  {ctx.created_at && (
                    <span> · {new Date(ctx.created_at).toLocaleDateString('pt-BR')}</span>
                  )}
                </span>
                <IndexBadge indexed={ctx.processing_status === 'processed'} />
              </div>
              <div style={{ fontSize: 13, color: C.neutralDim, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {ctx.summary ?? ctx.notes ?? ''}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Adicionar nota */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.neutralDim, marginBottom: 12, letterSpacing: '0.04em' }}>
          NOTAS DE DISCOVERY
        </div>
        <MutationError error={upsertContext.error} onDismiss={() => upsertContext.reset()} />
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Resumo da reunião de discovery, decisões tomadas, contexto do negócio..."
          rows={4}
          aria-label="Notas de discovery"
          style={{
            width: '100%',
            padding: '10px 14px',
            backgroundColor: C.black,
            border: `1px solid ${C.blackBorder}`,
            borderRadius: 6,
            color: C.neutral,
            fontSize: 13,
            resize: 'vertical',
            lineHeight: 1.5,
            marginBottom: 12,
          }}
        />
        <Btn
          variant="outline"
          size="sm"
          onClick={handleSaveContext}
          disabled={!notes.trim() || upsertContext.isPending}
          aria-busy={upsertContext.isPending}
        >
          {upsertContext.isPending ? 'Salvando...' : 'Salvar notas'}
        </Btn>
      </Card>

      {/* Documentos */}
      <Card style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.neutralDim, marginBottom: 4, letterSpacing: '0.04em' }}>
          DOCUMENTOS DO PROJETO
        </div>
        <p style={{ fontSize: 12, color: C.neutralMuted, margin: '0 0 12px' }}>
          Suba especificações em texto (<strong>.md</strong>, .txt, .json, .yaml, .csv…) —
          o conteúdo é guardado no projeto e usado como fonte de escopo. Você pode
          <strong> excluir</strong> qualquer documento.
        </p>

        {documentsQuery.isLoading && <LoadingSpinner label="Carregando documentos..." size={16} />}
        {documentsQuery.error && (
          <QueryError error={documentsQuery.error} onRetry={() => documentsQuery.refetch()} />
        )}
        <MutationError error={deleteDocument.error} onDismiss={() => deleteDocument.reset()} />

        {docs.length === 0 && !documentsQuery.isLoading && (
          <div style={{ fontSize: 13, color: C.neutralMuted, marginBottom: 16 }}>
            Nenhum documento adicionado.
          </div>
        )}

        {docs.map((doc, i) => (
          <div key={doc.id ?? i} style={{ borderBottom: `1px solid ${C.blackBorder}` }}>
            <div className="flex items-center gap-3 py-2">
              <FileText size={14} style={{ color: C.neutralMuted, flexShrink: 0 }} aria-hidden="true" />
              <span style={{ flex: 1, fontSize: 13, color: C.neutral, wordBreak: 'break-all' }}>
                {doc.filename ?? doc.title ?? 'documento'}
              </span>
              {doc.size_bytes ? (
                <span style={{ fontSize: 11, color: C.neutralMuted }}>{formatBytes(doc.size_bytes)}</span>
              ) : null}
              <Badge color={C.cyber} light>{TAG_TO_LABEL[doc.doc_tag] ?? doc.doc_tag ?? 'Outro'}</Badge>
              <IndexBadge indexed={doc.indexed === true} />
              {doc.content_text ? (
                <Btn
                  variant="ghost"
                  size="sm"
                  icon={Eye}
                  onClick={() => setPreviewId(previewId === doc.id ? null : doc.id)}
                  aria-label={previewId === doc.id ? 'Ocultar conteúdo' : 'Ver conteúdo'}
                >
                  {previewId === doc.id ? 'Ocultar' : 'Ver'}
                </Btn>
              ) : null}
              {confirmDel === doc.id ? (
                <span className="flex items-center gap-1">
                  <Btn
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteDoc(doc.id)}
                    disabled={deleteDocument.isPending}
                    aria-busy={deleteDocument.isPending}
                  >
                    {deleteDocument.isPending ? 'Excluindo…' : 'Confirmar'}
                  </Btn>
                  <Btn variant="ghost" size="sm" onClick={() => setConfirmDel(null)}>
                    Cancelar
                  </Btn>
                </span>
              ) : (
                <Btn
                  variant="ghost"
                  size="sm"
                  icon={Trash2}
                  onClick={() => setConfirmDel(doc.id)}
                  aria-label={`Excluir ${doc.filename ?? 'documento'}`}
                >
                  Excluir
                </Btn>
              )}
            </div>
            {previewId === doc.id && doc.content_text && (
              <pre
                style={{
                  margin: '0 0 10px',
                  padding: 12,
                  maxHeight: 240,
                  overflow: 'auto',
                  backgroundColor: C.black,
                  border: `1px solid ${C.blackBorder}`,
                  borderRadius: 6,
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: C.neutralDim,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                }}
              >
                {doc.content_text}
              </pre>
            )}
          </div>
        ))}

        {/* Upload de arquivo — envia automaticamente ao escolher (sem passo extra) */}
        <div style={{ marginTop: 16 }}>
          <MutationError error={addDocument.error} onDismiss={() => addDocument.reset()} />

          <input
            ref={fileInputRef}
            id="doc-file"
            type="file"
            accept=".md,.markdown,.txt,.json,.yaml,.yml,.csv,.html,.htm,.xml,text/markdown,text/plain,application/json,application/yaml,text/csv,text/html,application/xml"
            onChange={handleFilePick}
            style={{ display: 'none' }}
          />

          <div className="flex items-end gap-3">
            <div style={{ width: 180 }}>
              <label
                htmlFor="doc-type"
                style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}
              >
                Tipo do documento
              </label>
              <select
                id="doc-type"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                disabled={addDocument.isPending}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  backgroundColor: C.black,
                  border: `1px solid ${C.blackBorder}`,
                  borderRadius: 6,
                  color: C.neutral,
                  fontSize: 13,
                }}
              >
                {DOC_TYPE_OPTIONS.map((o) => <option key={o.tag} value={o.tag}>{o.label}</option>)}
              </select>
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={addDocument.isPending}
              style={{
                flex: 1,
                padding: '16px 14px',
                backgroundColor: C.black,
                border: `1px dashed ${addDocument.isPending ? C.cyber : C.blackBorder}`,
                borderRadius: 8,
                color: addDocument.isPending ? C.cyber : C.neutralDim,
                fontSize: 13,
                cursor: addDocument.isPending ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Upload size={16} aria-hidden="true" />
              {addDocument.isPending ? 'Enviando…' : 'Escolher arquivo (envia automaticamente)'}
            </button>
          </div>

          {fileError && (
            <div style={{ fontSize: 12, color: C.hotLight, marginTop: 8 }} role="alert">
              {fileError}
            </div>
          )}
        </div>
      </Card>

      {/* Navegação */}
      <div className="flex items-center justify-between">
        <Btn variant="ghost" size="md" onClick={onBack}>
          Voltar
        </Btn>
        <Btn variant="primary" size="lg" icon={ArrowRight} onClick={onNext}>
          Continuar
        </Btn>
      </div>
    </div>
  );
}
